import axios from 'axios';
import { mockInitData } from 'templates/initData';
import { clearToken, extractAndStoreToken } from './tokenManager';
import { getViteEnv } from 'utils/viteEnv';

const axiosServices = axios.create({
  baseURL: getViteEnv().VITE_USE_MOCK_INIT_DATA === 'true' ? getViteEnv().VITE_API_URL_TEST : getViteEnv().VITE_API_URL,
  // withCredentials: true, // Include HttpOnly cookies in requests
  timeout: 30000 // 30 seconds
});

let isFlushing = false;
let isRefreshing = false;
let failedQueue = [];
let refreshPromise = null;

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) prom.reject(error);
    else {
      prom.originalRequest.headers.Authorization = `Bearer ${token}`;
      prom.resolve(axiosServices(prom.originalRequest));
    }
  });
  failedQueue = [];
};

function saveErrorLocally(log) {
  const sanitized = {
    endpoint: log.endpoint,
    method: log.method,
    status: log.status,
    time: log.time,
    error: (typeof log.error === 'string' ? log.error : log.error?.message || 'Unknown error').slice(0, 2000)
  };

  const existing = JSON.parse(localStorage.getItem('pendingLogs') || '[]');
  if (existing.length < 50) {
    existing.push(sanitized);
    localStorage.setItem('pendingLogs', JSON.stringify(existing));
  }
}

const VALID_METHODS = ['GET', 'POST', 'PUT', 'DELETE', 'PATCH', 'HEAD', 'OPTIONS'];

function sanitizeErrorLog(log) {
  const method = typeof log.method === 'string' ? log.method.toUpperCase() : 'GET';
  const status = Number.isInteger(log.status) && log.status >= 100 && log.status <= 599 ? log.status : 500;
  const endpoint = typeof log.endpoint === 'string' && log.endpoint.length ? log.endpoint : 'unknown';
  const time = typeof log.time === 'string' && log.time.length ? log.time : new Date().toISOString();
  const errorMessage =
    typeof log.error === 'string' ? log.error.slice(0, 100) : log.error?.message ? log.error.message.slice(0, 100) : 'Unknown client error';

  return {
    endpoint,
    method: VALID_METHODS.includes(method) ? method : 'GET',
    status,
    time,
    error: errorMessage
  };
}

async function sendErrorLog(log) {
  try {
    await axiosServices.post('/api/log-client-error', sanitizeErrorLog(log));
  } catch {
    saveErrorLocally(log);
  }
}

async function flushPendingLogs() {
  if (isFlushing) return;

  const raw = JSON.parse(localStorage.getItem('pendingLogs') || '[]');
  if (!raw.length) return;

  const validMethods = ['GET', 'POST', 'PUT', 'DELETE', 'PATCH', 'HEAD', 'OPTIONS'];
  const pending = raw.filter(
    (log) =>
      typeof log.error === 'string' &&
      typeof log.status === 'number' &&
      log.status >= 100 &&
      log.status <= 599 &&
      typeof log.endpoint === 'string' &&
      validMethods.includes(log.method) &&
      typeof log.time === 'string'
  );

  // Discard any logs that can't pass backend validation — don't let stale/malformed
  // entries block the queue forever.
  if (!pending.length) {
    localStorage.removeItem('pendingLogs');
    return;
  }

  isFlushing = true;
  try {
    await axiosServices.post('/api/log-client-error/batch', pending);
    localStorage.removeItem('pendingLogs');
  } catch {
    // Always clear on failure — the interceptor rejects with a string (not an Error),
    // so err?.response?.status is always undefined and can't be used for branching.
    // Keeping malformed logs would cause an infinite retry loop.
    localStorage.removeItem('pendingLogs');
  } finally {
    isFlushing = false;
  }
}

export const forceRefreshToken = async () => {
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    try {
      const useMockData = getViteEnv().VITE_USE_MOCK_INIT_DATA === 'true';
      const currentPlayer = localStorage.getItem('currentMockPlayer') || 'player1';
      const initData = useMockData
        ? currentPlayer === 'player1'
          ? mockInitData.initDataPlayer1
          : mockInitData.initDataPlayer2
        : window.Telegram?.WebApp?.initData;

      if (!initData) throw new Error('No initData available for refresh');

      const { data } = await axios.post(
        '/api/initdata',
        { initdata: initData },
        {
          baseURL: axiosServices.defaults.baseURL
        }
      );

      extractAndStoreToken(data);
      axiosServices.defaults.headers.common.Authorization = `Bearer ${data.token}`;

      return data.token;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
};

axiosServices.interceptors.response.use(
  async (response) => {
    await flushPendingLogs();
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    const status = error?.response?.status || 0;

    if (status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ originalRequest, resolve, reject });
        });
      }

      isRefreshing = true;

      try {
        const token = await forceRefreshToken();
        processQueue(null, token);
        originalRequest.headers.Authorization = `Bearer ${token}`;
        return axiosServices(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        clearToken();
        delete axiosServices.defaults.headers.common.Authorization;
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    } else {
      const endpoint = (error?.config?.url || 'unknown').slice(0, 256);
      const rawMethod = (error?.config?.method || '').toUpperCase();
      const validMethods = ['GET', 'POST', 'PUT', 'DELETE', 'PATCH', 'HEAD', 'OPTIONS'];
      const method = validMethods.includes(rawMethod) ? rawMethod : 'GET';
      const logStatus = status >= 100 && status <= 599 ? status : 599;

      const log = {
        endpoint,
        method,
        error: (error?.message || 'Unknown error').slice(0, 2000),
        status: logStatus,
        time: new Date().toISOString()
      };
      sendErrorLog(log);
    }

    return Promise.reject(error?.response?.data.message || 'Something went wrong');
  }
);

export default axiosServices;
